import * as React from 'react';
import { PriceAgreementViewModel } from '@models/Vendor';
import ReactTable, { Column } from 'react-table';
import { LocalForm, Control, Errors, actions } from 'react-redux-form';
import { getDefaultErrorMessages, hasValue } from '@common/validators';
import { Select, DynamicSelectIdentifier, ModalProps, CheckboxInline } from '@common/components';
import { OptionsViewModel } from '@models/common.models';
import { TooltipComponent } from '@common/components/tooltip/tooltip.component';
import { Dispatch } from 'react-redux';
import { parseCurrency } from '@common/parsers';
import * as classes from '../vendor-trade-maintenance.scss';
import { findDOMNode } from 'react-dom';

export interface TradePriceAgreementComponentProperties {
  readonly id: number;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly isTab?: boolean;
  readonly readOnly?: boolean;

  readonly searchPriceAgreements: (
    id: number,
    query: string | null,
    pageNumber: number,
    pageSize: number,
    filterBy: string,
    orderBy: string,
    orderDirection: string,
    filters?: OptionsViewModel
  ) => void;

  readonly savePriceAgreement: (vendorId: number, agreement: PriceAgreementViewModel, callback: Function) => void;

  readonly priceAgreementsSearchResults: PriceAgreementViewModel[];

  readonly message: string;
  readonly priceAgreementsSearchQuery: string | null;
  readonly priceAgreementsSearchLoading: boolean;
  readonly priceAgreementsSearchPageNumber: number;
  readonly priceAgreementsSearchPageSize: number;
  readonly priceAgreementsSearchPageCount: number;
  readonly priceAgreementsSearchResultsTotalCount: number;

  readonly cviFilter: OptionsViewModel;
  readonly paymentType: string;
  readonly cviOptions: Array<OptionsViewModel> | null;
}

interface FormModel {
  readonly query: string;
  readonly cviFilter: number;
  readonly priceAgreementPaymentType: string;
}

interface EditPriceAgreementComponentProperties {
  readonly agreement: PriceAgreementViewModel;
  readonly toggleModal: (modalProps?: ModalProps) => void;
  readonly vendorId: number;
  readonly savePriceAgreement: (vendorId: number, agreement: PriceAgreementViewModel, callback: Function) => void;
  readonly callback: Function;
}
class EditPriceAgreementComponent extends React.Component<EditPriceAgreementComponentProperties> {
  /**
   * Modal for editing a price agreement
   *
   * @returns
   * @memberof EditPriceAgreementComponent
   */
  render() {
    const { agreement, toggleModal, callback } = this.props;
    return (
      <LocalForm
        className="form-horizontal"
        hideNativeErrors={true}
        validateOn="submit"
        model="PriceAgreementEditForm"
        onSubmit={formValues => {
          this.props.savePriceAgreement(this.props.vendorId, { ...agreement, ...formValues }, callback);
          this.props.toggleModal();
        }}
      >
        <div className="row">
          <div className="col-md-3 mb-10">
            <label htmlFor=".name">Name</label>
            <h5 id="name">{agreement.item}</h5>
          </div>
          <div className="col-md-3 mb-10">
            <label htmlFor=".standardPrice">Standard Vendor Group Price</label>
            <h5>${agreement.standardPrice}</h5>
          </div>
          <div className="col-md-3 mb-10">
            <label htmlFor="PriceAgreementEditForm.vendorPrice">Vendor Modified Price</label>
            <Control.text
              model="PriceAgreementEditForm.vendorPrice"
              defaultValue={agreement.vendorPrice ? agreement.vendorPrice.toString() : '--'}
              parser={parseCurrency}
              type="number"
            />
          </div>
          <div className="col-md-3 mb-10">
            <label htmlFor=".hourly">Payment Type</label>
            <div className="row mt-10">
              <div className="col-xs-6">
                <CheckboxInline>
                  <Control.radio model=".hourly" value="true" validators={{ required: hasValue }} />
                  <label htmlFor=".hourly">T&M</label>
                </CheckboxInline>
                <CheckboxInline>
                  <Control.radio model=".hourly" value="false" validators={{ required: hasValue }} />
                  <label htmlFor=".hourly">Flat</label>
                </CheckboxInline>
                <Errors
                  className="error"
                  model=".hourly"
                  show={{ touched: true, focus: false }}
                  messages={getDefaultErrorMessages()}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-12">
          <div className={`row end-xs mt-10 mb-10 ${classes.buttons}`}>
            <button className="btn btn-secondary" type="button" onClick={() => toggleModal()}>
              Cancel
            </button>
            <button className="btn btn-primary" type="submit">
              Save
            </button>
          </div>
        </div>
      </LocalForm>
    );
  }
}

export class TradePriceAgreementComponent extends React.Component<TradePriceAgreementComponentProperties> {
  /**
   * Search component for price agreements
   *
   * @type {Dispatch<any>}
   * @memberof TradePriceAgreementComponent
   */
  formDispatch: Dispatch<any>;
  isInitialized: boolean = false;
  form: any;
  componentDidMount() {
    if (!this.props.priceAgreementsSearchResults) {
      this.handleSearch('', '');
    }
    if (this.props.cviFilter) {
      this.formDispatch(actions.change('priceAgreementsSearch.cviFilter', this.props.cviFilter));
    }
    if (this.props.paymentType) {
      this.formDispatch(actions.change('priceAgreementsSearch.paymentType', this.props.paymentType));
    }
  }
  /**
   * Stores ref for search form so
   * it can be programmatically submitted
   *
   * @param {*} node
   */
  attachNode = (node: any) => {
    this.form = findDOMNode(node);
  };
  attachDispatch = (dispatch: Dispatch<any>) => {
    this.formDispatch = dispatch;
  };

  /**
   * Toggle modal for editing a price agreement
   *
   * @param {PriceAgreementViewModel} agreement
   */
  renderModal = (agreement: PriceAgreementViewModel) => {
    this.props.toggleModal({
      title: 'Edit Price Agreement',
      component: EditPriceAgreementComponent,
      childProps: {
        vendorId: this.props.id,
        agreement,
        toggleModal: this.closeModal,
        savePriceAgreement: this.props.savePriceAgreement,
        callback: () => {
          // Redo search after edit to update content
          this.form.submit();
        }
      },
      hideSave: true,
      hideClose: true
    });
  };

  /**
   * Closes modal
   *
   */
  closeModal = () => {
    this.props.toggleModal();
  };

  render() {
    const columns: Column[] = [
      {
        accessor: (row: PriceAgreementViewModel) =>
          !this.props.readOnly ? (
            <a href="javascript:void(0);" onClick={() => this.renderModal(row)}>
              {row.item}
            </a>
          ) : (
            row.item
          ),
        id: 'item',
        Header: 'Name',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (row: PriceAgreementViewModel) => `$${row.standardPrice}`,
        id: 'standard',
        Header: 'Standard Vendor Group Price',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (row: PriceAgreementViewModel) => (row.vendorPrice ? `$${row.vendorPrice}` : 'N/A'),
        id: 'address',
        Header: 'Vendor Modified Price',
        sortable: true,
        defaultSortDesc: true
      },
      {
        accessor: (row: PriceAgreementViewModel) => (row.hourly ? 'T&M' : 'Flat Rate'),
        id: 'group',
        Header: 'Payment Type',
        sortable: true,
        defaultSortDesc: true
      }
    ];
    return (
      <div className="pb-10">
        <LocalForm
          ref={this.attachNode}
          className="form-horizontal"
          hideNativeErrors={true}
          onSubmit={(props: FormModel) => {
            this.handleSearch(
              props.query,
              props.priceAgreementPaymentType,
              this.props.cviOptions ? this.props.cviOptions.find(x => x.id === +props.cviFilter) : undefined
            );
          }}
          model="priceAgreementsSearch"
          getDispatch={(dispatch: Dispatch<any>) => this.attachDispatch(dispatch)}
        >
          <div className="row">
            <div className="col-md-6">{!this.props.isTab && <h2>Price Agreement</h2>}</div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="row text-left">
                <div className="col-md-4">
                  <label htmlFor=".cviFilter">Filter by CVI:</label>
                  <Select
                    id="cviFilter"
                    identifier={DynamicSelectIdentifier.CVIs}
                    model=".cviFilter"
                    validateOn="change"
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor=".priceAgreementPaymentType">Filter By Payment Type:</label>
                  <Control.select id="priceAgreementPaymentType" model=".priceAgreementPaymentType">
                    <option key={-1} value={-1}>
                      Select one...
                    </option>
                    <option value={0}>Time & Materials</option>
                    <option value={1}>Flat Rate</option>
                  </Control.select>
                </div>
                <div className="col-md-4">
                  <label htmlFor=".query">Search</label>
                  <TooltipComponent text="Search by item name" side="top" />
                  <Control.text model=".query" />
                  <Errors
                    className="error"
                    model=".query"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                  {this.props.priceAgreementsSearchPageCount * 10 > 10000 && (
                    <span> Your search is too broad. Narrow the search with more specific terms. </span>
                  )}
                </div>
              </div>
              <div className="row center-xs text-right">
                <button
                  className="btn btn-primary"
                  disabled={this.props.priceAgreementsSearchLoading}
                  onClick={() => this.form.submit}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </LocalForm>

        <div>
          <ReactTable
            onPageChange={() => {
              Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                element.scrollTop = 0;
              });
            }}
            className="-striped -highlight"
            columns={columns}
            data={this.props.priceAgreementsSearchResults}
            defaultPageSize={10}
            loading={this.props.priceAgreementsSearchLoading}
            manual={true}
            onFetchData={this.handleFetchData}
            pages={this.props.priceAgreementsSearchPageCount}
            previousText="Prev"
          />
        </div>
      </div>
    );
  }

  private handleFetchData = (state: {
    /**
     * handles data fetching for price agreements
     *
     * @type {number}
     */
    page: number;
    pageSize: number;
    sorted: Array<{ id: string; desc: boolean }>;
  }): void => {
    const orderBy = state.sorted[0] ? state.sorted[0].id : 'id';
    const orderDirection = state.sorted[0] ? (state.sorted[0].desc ? 'desc' : 'asc') : 'asc';
    const filters: OptionsViewModel | undefined =
      this.props.cviOptions && this.props.cviFilter
        ? this.props.cviOptions.find(x => x.id === this.props.cviFilter.id)
        : undefined;

    if (this.isInitialized) {
      this.props.searchPriceAgreements(
        this.props.id,
        this.props.priceAgreementsSearchQuery,
        state.page + 1,
        state.pageSize,
        this.props.paymentType,
        orderBy,
        orderDirection,
        filters
      );
    }

    this.isInitialized = true;
  };

  /**
   * search handler
   *
   * @param {(string | null)} query
   * @param {string} filterBy
   * @param {OptionsViewModel} [filters]
   */
  private handleSearch = (query: string | null, filterBy: string, filters?: OptionsViewModel): void => {
    this.isInitialized = true;
    let paymentType = '';
    if (+filterBy === 0) {
      paymentType = 'hourly';
    } else if (+filterBy === 1) {
      paymentType = 'flat';
    }

    this.props.searchPriceAgreements(
      this.props.id,
      query,
      1,
      this.props.priceAgreementsSearchPageSize,
      paymentType,
      'id',
      'asc',
      filters
    );
  };
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-trade-maintenance/_components/trade-price-agreement.component.tsx